import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import ChangePasswordForm from "@/components/admin/ChangePasswordForm";
import { Spinner } from "@/components/common/Primitives";
import { normalizeSettings } from "@/lib/settings";
import { contentService } from "@/services/contentService";
import { invalidateSiteContent } from "@/lib/invalidateQueries";

const FIELDS = [
  ["org_name", "Organisation Name"],
  ["motto", "Motto"],
  ["tagline", "Tagline"],
  ["contact_phone", "Contact Phone"],
  ["whatsapp", "WhatsApp Number"],
  ["office_hours", "Office Hours"],
  ["address", "Office Address", true],
  ["facebook", "Facebook URL"],
  ["instagram", "Instagram URL"],
  ["youtube", "YouTube URL"],
];

const EMAIL_LABELS = ["Primary email", "Email 2", "Email 3"];

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    contentService.getSettings().then(setSettings);
  }, []);

  if (!settings) return <Spinner className="py-24" />;

  const setEmail = (index, value) => {
    const contact_emails = [...(settings.contact_emails || ["", "", ""])];
    contact_emails[index] = value;
    setSettings({ ...settings, contact_emails });
  };

  async function save() {
    setSaving(true);
    try {
      await contentService.saveContent("settings", normalizeSettings(settings));
      await invalidateSiteContent();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-ink">Settings</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Organisation details and your admin account settings.
          </p>
        </div>
        <button className="btn-primary" onClick={save} disabled={saving} data-testid="save-settings-btn">
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>

      <div className="mt-7 bg-white rounded-xl border border-gray-200 shadow-card p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
        {FIELDS.map(([key, label, textarea]) => (
          <div key={key} className={textarea ? "sm:col-span-2" : ""}>
            <label className="label-bcs">{label}</label>
            {textarea ? (
              <textarea
                className="input-bcs min-h-[80px]"
                value={settings[key] || ""}
                onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                data-testid={`settings-${key}`}
              />
            ) : (
              <input
                className="input-bcs"
                value={settings[key] || ""}
                onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                data-testid={`settings-${key}`}
              />
            )}
          </div>
        ))}

        <div className="sm:col-span-2 pt-2 border-t border-gray-100">
          <p className="label-bcs mb-3">Contact emails</p>
          <div className="space-y-3">
            {EMAIL_LABELS.map((label, i) => (
              <div key={label}>
                <label className="text-xs font-medium text-ink-muted mb-1 block">{label}</label>
                <input
                  type="email"
                  className="input-bcs"
                  value={settings.contact_emails?.[i] || ""}
                  onChange={(e) => setEmail(i, e.target.value)}
                  placeholder="name@bcsabha.org"
                  data-testid={`settings-contact-email-${i}`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <ChangePasswordForm />
      </div>
    </div>
  );
}
